<template>
    <SnapDialog
        :width="528"
        :title="$t('patent-view.quickComment.title')"
        :onClose="onClose"
        mount-point=".patent-view-app"
        class="edit-quick-comment-dialog">
        <div slot="body" class="edit-quick-comment-dialog__body">
            <!--title-->
            <p class="edit-quick-comment-dialog__title">{{ $t('patent-view.quickComment.limit', { limit: countLimit }) }}</p>
            <!--列表-->
            <div v-if="commentList.length" class="edit-quick-comment-dialog__container">
                <div v-for="(item, index) in commentList" :key="index" class="edit-quick-comment-dialog__item">
                    <span class="edit-quick-comment-dialog__item-index">{{ index + 1 }}</span>
                    <div :class="{ 'edit-quick-comment-dialog__item-content': true, 'edit-quick-comment-dialog__item-content--edit': item.isEditing }">
                        <ElInput
                            v-model="item.content"
                            :disabled="isSubmitting"
                            :ref="`edit-input-${index}`"
                            maxlength="100"
                            class="edit-quick-comment-dialog__item-content-edit"
                            @focus="editItem(item)"
                            @blur="confirmEditItem(item, index)"
                            @keyup.enter.native="confirmEditItem(item, index)" />
                        <div class="edit-quick-comment-dialog__item-content-display">{{ item.content }}</div>
                    </div>
                    <ElTooltip
                        :visible-arrow="false"
                        :content="$t('Delete')"
                        :placement="'top'"
                        :open-delay="300"
                        popper-class="edit-quick-comment-dialog__item-delete__tooltip">
                        <PtIcon icon="SolidDelete" :class="{ 'edit-quick-comment-dialog__item-delete': true, 'edit-quick-comment-dialog__item-delete--disabled': isSubmitting }" @click="deleteItem(item, index)"/>
                    </ElTooltip>
                </div>
            </div>
            <!--新增-->
            <div v-if="this.commentList.length < countLimit" class="edit-quick-comment-dialog__add">
                <ElInput v-model="commentText" :disabled="isSubmitting" ref="add-input" maxlength="100" class="edit-quick-comment-dialog__add-input" @keyup.enter.native="addItem" />
                <button :disabled="disabledAdd" class="edit-quick-comment-dialog__add-button" @click="addItem">
                    <PtIcon icon="SolidAddSmall" />
                    <span>{{ $t('Add') }}</span>
                </button>
            </div>
        </div>
        <div slot="footer" class="edit-quick-comment-dialog__footer">
            <button type="button" class="edit-quick-comment-dialog__cancel" @click="onClose">{{ $t('fileWrapperDialog.cancel') }}</button>
            <button :disabled="disabledSubmit" type="submit" class="edit-quick-comment-dialog__confirm" @click="submit">{{$t('fileWrapperDialog.submit')}}</button>
        </div>
    </SnapDialog>
</template>

<script>
import SnapDialog from 'library-@/components/snap-ui/dialog';
import { Input, Tooltip } from 'element-ui';
import { Icon } from 'patsnap-biz';
import { some, get } from 'lodash';
import QuickCommentService from '@/services/quick-comment-service';
import { useCommentDialog } from '@/components/dialog/CommentDialog/composables/use-comment-dialog';
import '@patsnap-ui/icon/assets/solid/delete.svg';
import '@patsnap-ui/icon/assets/solid/AddSmall.svg';

export default {
    name: 'EditQuickCommentDialog',
    components: {
        SnapDialog,
        ElInput: Input,
        ElTooltip: Tooltip,
        PtIcon: Icon
    },
    setup() {
        const {
            quickCommentList,
            quickCommentId,
            setIsShowingEditQuickComment,
            updateQuickComment,
        } = useCommentDialog();

        return {
            quickCommentList,
            quickCommentId,
            setIsShowingEditQuickComment,
            updateQuickComment,
        };
    },
    data() {
        return {
            commentText: '',
            tempText: '',
            commentList: [],
            countLimit: 5,
            isSubmitting: false
        };
    },
    computed: {
        disabledAdd() {
            return !this.commentText.trim().length || this.isSubmitting;
        },
        disabledSubmit() {
            return some(this.commentList, item => item.isEditing) || this.isSubmitting;
        }
    },
    methods: {
        addItem() {
            this.commentList.push({ content: this.commentText, isEditing: false });
            this.commentText = '';
            const input = get(this.$refs, '[add-input]');
            input && input.blur();
        },
        editItem(item) {
            item.isEditing = true;
            this.tempText = item.content;
        },
        confirmEditItem(item, index) {
            if (!item.content.trim().length) {
                item.content = this.tempText;
                this.tempText = '';
            }
            const input = get(this.$refs, `edit-input-${index}[0]`);
            input && input.blur();
            item.isEditing = false;
        },
        deleteItem(item, index) {
            if (this.isSubmitting) return;
            this.commentList.splice(index, 1);
        },
        onClose() {
            this.setIsShowingEditQuickComment(false);
        },
        async submit() {
            this.isSubmitting = true;
            try {
                const result = await QuickCommentService.editQuickComments(this.quickCommentId, { comment_texts: this.commentList.map(({ content }) => ({ comment_text: content })) });
                this.updateQuickComment(result);
                this.isSubmitting = false;
                this.onClose();
            } catch (e) {
                this.isSubmitting = false;
            }
        }
    },
    watch: {
        quickCommentList: {
            handler(val) {
                this.commentList = val.map(text => ({ content: text, isEditing: false }));
            },
            immediate: true
        }
    }
};
</script>

<style lang="scss">
.edit-quick-comment-dialog {
    /*modal样式覆盖*/
    .modal-container {
        padding: 20px;
        background-image: none;
    }
    .modal-header {
        font-size: 16px;
        font-weight: 500;
        color: #061632;
    }
    .modal-body {
        margin-top: 24px !important;
    }
    .modal-footer {
        padding: 0;
        padding-top: 32px;
        text-align: right;
    }
    .modal-close-btn {
        right: 20px;
        /* 改component中ui 组件时需要改一下 */
        .patsnap-ui-icon {
            height: 24px !important;
            width: 24px !important;
            fill: #52607A !important;
        }
    }

    /*element样式覆盖*/
    .el-input__inner {
        height: 32px;
        line-height: 32px;
        color: #061632 !important;
    }

    /*主体*/
    &__body {
        padding: 0 8px;
        color: #061632;
    }
    &__title {
        color: #5E6C84;
        margin-bottom: 24px;
    }
    &__add {
        display: flex;
        &-button {
            cursor: pointer;
            border-radius: 4px;
            background-color: #F4F5F7;
            border: 1px solid #B3BAC5;
            color: #061632;
            height: 32px;
            line-height: 30px;
            flex-shrink: 0;
            margin-left: 10px;
            padding: 0 16px 0 30px;
            position: relative;
            .patsnap-biz-icon {
                position: absolute;
                top: 50%;
                left: 8px;
                transform: translateY(-50%);
                -ms-transform: translateY(-50%);
            }
            &:hover {
                background-color: #dfe1e6;
            }
            &:disabled {
                color: #B3BAC5;
                background-color: #F4F5F7;
                cursor: not-allowed;
                .patsnap-biz-icon {
                    fill: #B3BAC5 !important;
                }
            }
        }
    }
    &__item {
        display: flex;
        align-items: center;
        margin-bottom: 8px;
        &-index {
            flex-shrink: 0;
            color: #B3BAC5;
            width: 20px;
        }
        &-content {
            flex: 1;
            overflow: hidden;
            &-display {
                height: 32px;
                line-height: 30px;
                white-space: nowrap;
                overflow: hidden;
                text-overflow: ellipsis;
                padding-left: 10px;
                border: 1px solid transparent;
                display: block !important;
            }
            &-edit {
                display: none !important;
            }
            &:hover, &--edit {
                .edit-quick-comment-dialog__item-content-display {
                    display: none !important;
                }
                .edit-quick-comment-dialog__item-content-edit {
                    display: block !important;
                }
            }
        }
        &-delete {
            flex-shrink: 0;
            border-radius: 4px;
            cursor: pointer;
            margin-left: 24px;
            &:hover {
                background-color: #EBECF0;
            }
            &--disabled {
                cursor: not-allowed;
            }
        }
    }

    /*button*/
    &__confirm, &__cancel{
        font-size: 16px;
        padding: 0 16px;
        min-width: 80px;
        text-align: center;
        height: 32px;
        line-height: 32px;
        border-radius: 4px;
        cursor: pointer;
        border: none;
    }
    button.edit-quick-comment-dialog__confirm {
        background-color: #45a321;
        border-color: transparent;
        color: #fff;
        &:hover {
            background-color: #3d921d;
            border-color: transparent;
        }
        &:disabled {
            background-color: #c6e3bb;
            cursor: not-allowed;
        }
    }
    button.edit-quick-comment-dialog__cancel {
        text-align: center;
        background-color: #ebecf0;
        border-color: transparent;
        margin-right: 8px;
        &:hover {
            background-color: #dfe1e6;
            border-color: transparent;
        }
    }
}
.edit-quick-comment-dialog__item-delete__tooltip {
    &[x-placement^=top] {
        margin-bottom: 4px !important;
    }
    &[x-placement^=bottom] {
        margin-top: 4px !important;
    }
}
</style>
